import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'app-custom-calendar',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cal-backdrop" (click)="onBackdropClick($event)">
      <div
        class="cal-panel"
        [style.top.px]="panelTop"
        [style.left.px]="panelLeft"
        (click)="$event.stopPropagation()"
      >
        <div class="cal-header">
          <button
            class="cal-nav"
            (click)="prevMonth()"
            title="Previous month"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <div class="cal-header-center">
            <div
              class="cal-dropdown-wrap"
              (click)="toggleMonthDropdown($event)"
            >
              <span class="cal-header-label">{{ monthNames[viewMonth] }}</span>
              <svg
                class="cal-chevron"
                [class.open]="showMonthDrop"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <div
                class="cal-drop-list"
                *ngIf="showMonthDrop"
                (click)="$event.stopPropagation()"
              >
                <div
                  class="cal-drop-item"
                  *ngFor="let m of monthNames; let i = index"
                  [class.active]="i === viewMonth"
                  (click)="selectMonth(i, $event)"
                >
                  {{ m }}
                </div>
              </div>
            </div>
            <div class="cal-dropdown-wrap" (click)="toggleYearDropdown($event)">
              <span class="cal-header-label">{{ viewYear }}</span>
              <svg
                class="cal-chevron"
                [class.open]="showYearDrop"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <div
                class="cal-drop-list year-list"
                *ngIf="showYearDrop"
                (click)="$event.stopPropagation()"
              >
                <div
                  class="cal-drop-item"
                  *ngFor="let y of yearRange"
                  [class.active]="y === viewYear"
                  (click)="selectYear(y, $event)"
                >
                  {{ y }}
                </div>
              </div>
            </div>
          </div>

          <button
            class="cal-nav"
            (click)="nextMonth()"
            title="Next month"
            type="button"
            [disabled]="isNextMonthDisabled()"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18l6-6-6-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <div class="cal-weekdays" [class.weekdays-5day]="disableWeekends">
          <span *ngFor="let d of getFilteredDayLabels()">{{ d }}</span>
        </div>
        <div class="cal-grid" [class.grid-5day]="disableWeekends">
          <button
            *ngFor="let cell of getFilteredCells()"
            type="button"
            class="cal-cell"
            [class.other-month]="cell.otherMonth"
            [class.today]="cell.isToday"
            [class.selected]="cell.isSelected"
            [class.in-range]="cell.inRange"
            [class.range-start]="cell.isRangeStart"
            [class.range-end]="cell.isRangeEnd"
            [class.disabled]="cell.disabled"
            [disabled]="cell.disabled"
            (click)="selectDate(cell)"
            (mouseenter)="onCellHover(cell)"
          >
            {{ cell.day }}
          </button>
        </div>

        <div class="cal-footer">
          <button class="cal-btn-ghost" type="button" (click)="goToday()">
            Today
          </button>
          <button class="cal-btn-ghost" type="button" (click)="clearDate()">
            Clear
          </button>
          <button class="cal-btn-primary" type="button" (click)="onDoneClick()">
            Done
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .cal-backdrop {
        position: fixed;
        inset: 0;
        z-index: 10000;
        background: transparent;
      }

      .cal-panel {
        position: fixed;
        overflow: visible;
        font-family: 'Montserrat', sans-serif;
        z-index: 10001;
        background: #292929;
        border: 1px solid rgba(255, 255, 255, 0.07);
        border-radius: 16px;
        width: 300px;
        overflow: hidden;
        box-shadow:
          0 24px 64px rgba(0, 0, 0, 0.6),
          0 0 0 1px rgba(255, 255, 255, 0.04);
        animation: _ngcontent-ng-c1982331633_tp-slide-up 0.28s
          cubic-bezier(0.34, 1.56, 0.64, 1);
      }

      @keyframes calIn {
        from {
          opacity: 0;
          transform: translateY(-8px) scale(0.97);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      .cal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 14px 10px;
        border-bottom: 1px solid #1e1e1e;
      }

      .cal-nav {
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #1a1a1a;
        border: 1px solid #2a2a2a;
        border-radius: 8px;
        color: #aaa;
        cursor: pointer;
        transition: all 0.15s;
        flex-shrink: 0;
      }
      .cal-nav:hover {
        background: #252525;
        color: #fff;
        border-color: #3a3a3a;
      }
      .cal-nav:disabled {
        background: #161616;
        color: #4a4a4a;
        border-color: #222;
        cursor: not-allowed;
      }

      .cal-header-center {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .cal-dropdown-wrap {
        position: relative;
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 5px 8px;
        border-radius: 7px;
        cursor: pointer;
        transition: background 0.15s;
        user-select: none;
      }
      .cal-dropdown-wrap:hover {
        background: #1e1e1e;
      }

      .cal-header-label {
        font-size: 13px;
        font-weight: 600;
        color: #f0f0f0;
        letter-spacing: 0.01em;
        white-space: nowrap;
      }

      .cal-chevron {
        color: #888;
        transition: transform 0.2s;
        flex-shrink: 0;
      }
      .cal-chevron.open {
        transform: rotate(180deg);
      }

      .cal-drop-list {
        position: absolute;
        top: calc(100% + 4px);
        left: 50%;
        transform: translateX(-50%);
        background: #1a1a1a;
        border: 1px solid #2a2a2a;
        border-radius: 8px;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.85);
        z-index: 10010;
        max-height: 200px;
        overflow-y: auto;
        min-width: 120px;
        animation: dropIn 0.12s ease;
        scrollbar-width: none;
      }
      .cal-drop-list::-webkit-scrollbar {
        display: none;
      }
      .year-list {
        min-width: 80px;
      }

      @keyframes dropIn {
        from {
          opacity: 0;
          transform: translateX(-50%) translateY(-4px);
        }
        to {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      }

      .cal-drop-item {
        padding: 8px 14px;
        font-size: 13px;
        color: #ccc;
        cursor: pointer;
        transition: background 0.1s;
        text-align: center;
        white-space: nowrap;
      }
      .cal-drop-item:hover {
        background: #252525;
        color: #fff;
      }
      .cal-drop-item.active {
        background: #2cc3e8;
        color: #fff;
        font-weight: 600;
      }

      .cal-weekdays {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        padding: 10px 12px 4px;
      }

      .cal-weekdays.weekdays-5day {
        grid-template-columns: repeat(5, 1fr);
        padding: 10px 12px 4px;
      }

      .cal-weekdays span {
        text-align: center;
        font-size: 10px;
        font-weight: 700;
        color: #555;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .cal-grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        padding: 4px 12px 10px;
        justify-items: center;
      }

      .cal-grid.grid-5day {
        grid-template-columns: repeat(5, 1fr);
        gap: 2px;
        padding: 4px;
        .cal-cell {
          aspect-ratio: 1.115;
        }
      }

      .cal-cell {
        aspect-ratio: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        color: #d0d0d0;
        background: transparent;
        border: 1px solid transparent;
        cursor: pointer;
        transition: all 0.12s;
        font-family: 'Montserrat', sans-serif;
        padding: 0;
        position: relative;
        min-height: 35px;
        min-width: 35px;
      }
      .cal-cell:hover:not(:disabled):not(.selected):not(.in-range) {
        background: #1e1e1e;
        border-color: #333;
        color: #fff;
      }
      .cal-cell.other-month {
        color: #3a3a3a;
      }
      .cal-cell.other-month:hover:not(:disabled) {
        background: #181818;
        color: #666;
      }
      .cal-cell.today {
        border-color: #2cc3e8;
        color: #7ab4ff;
        font-weight: 600;
        font-size: 16px;
      }
      .cal-cell.today:not(.selected) {
        background: rgba(43, 112, 219, 0.1);
      }

      // /* Range selection styles */
      // .cal-cell.in-range {
      //   background: rgba(43, 112, 219, 0.15);
      //   border-radius: 0;
      // }

      // .cal-cell.range-start {
      //   background: #2B70DB !important;
      //   border-color: #2B70DB !important;
      //   color: #fff !important;
      //   font-weight: 700;
      //   border-top-left-radius: 8px;
      //   border-bottom-left-radius: 8px;
      //   border-top-right-radius: 0;
      //   border-bottom-right-radius: 0;
      // }

      // .cal-cell.range-end {
      //   background: #2B70DB !important;
      //   border-color: #2B70DB !important;
      //   color: #fff !important;
      //   font-weight: 700;
      //   border-top-right-radius: 8px;
      //   border-bottom-right-radius: 8px;
      //   border-top-left-radius: 0;
      //   border-bottom-left-radius: 0;
      // }

      .cal-cell.range-start.range-end {
        border-radius: 8px;
      }

      .cal-cell.selected {
        background: #2cc3e8 !important;
        border-color: #2cc3e8 !important;
        color: #fff !important;
        font-weight: 700;
        box-shadow: none;
        z-index: 2;
      }

      .cal-cell:disabled,
      .cal-cell.disabled {
        color: rgba(255, 255, 255, 0.2);
        cursor: not-allowed;
        pointer-events: none;
        background: transparent;
        opacity: 0.35;
      }

      .cal-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        padding: 10px 14px;
        border-top: 1px solid #1e1e1e;
      }
      .cal-btn-ghost {
        padding: 5px 10px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        font-family: 'Montserrat', sans-serif;
        color: #888;
        background: transparent;
        border: 1px solid #2a2a2a;
        cursor: pointer;
        transition: all 0.15s;
      }
      .cal-btn-ghost:hover {
        background: #1e1e1e;
        color: #ccc;
        border-color: #3a3a3a;
      }

      .cal-btn-primary {
        padding: 5px 14px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        font-family: 'Montserrat', sans-serif;
        background: #2cc3e8;
        color: #fff;
        border: none;
        cursor: pointer;
        transition: background 0.15s;
      }
      .cal-btn-primary:hover {
        opacity: 0.8;
      }
    `,
  ],
})
export class CustomCalendarComponent implements OnInit, OnChanges {
  @Input() value: string | null = null;
  @Input() min: string | null = null;
  @Input() max: string | null = null;
  @Input() anchorRect: DOMRect | null = null;
  @Input() rangeStartDate: string | null = null;
  @Input() rangeEndDate: string | null = null;
  @Input() isEndDateCalendar: boolean = false;
  @Input() disableWeekends: boolean = false;
  // Used once at init to open the calendar on a specific month (does not trigger ngOnChanges re-renders)
  @Input() initialViewDate: string | null = null;

  @Output() dateSelected = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();

  private tempSelectedDate: string | null = null;
  private hoveredDate: string | null = null;
  @Input() yearRangeList: number[] | null = null;

  viewMonth = new Date().getMonth();
  viewYear = new Date().getFullYear();
  cells: CalCell[] = [];
  panelTop = 0;
  panelLeft = 0;
  showMonthDrop = false;
  showYearDrop = false;

  readonly monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  readonly dayLabels = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  readonly weekdayLabels = ['Mo', 'Tu', 'We', 'Th', 'Fr'];
  yearRange: number[] = [];

  private todayISO: string;

  constructor(
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
  ) {
    this.todayISO = this.dateToISO(new Date());
  }

  ngOnInit(): void {
    this.buildYearRange();
    this.tempSelectedDate = this.value;

    if (this.value) {
      const parts = this.parseISO(this.value);
      if (parts) {
        this.viewMonth = parts.month;
        this.viewYear = parts.year;
      }
    } else if (this.initialViewDate) {
      // Open to the month of the provided date (e.g. start date for end-date calendar)
      const parts = this.parseISO(this.initialViewDate);
      if (parts) {
        this.viewMonth = parts.month;
        this.viewYear = parts.year;
      }
    }
    this.buildGrid();
    this.position();
    setTimeout(() => this.scrollYearIntoView(), 50);
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['anchorRect'] && !ch['anchorRect'].firstChange) {
      this.position();
    }
    if (ch['value'] && !ch['value'].firstChange) {
      this.tempSelectedDate = this.value;
      if (this.value) {
        const parts = this.parseISO(this.value);
        if (parts) {
          this.viewMonth = parts.month;
          this.viewYear = parts.year;
        }
      }
      this.buildGrid();
    }
    if (
      ch['rangeStartDate'] ||
      ch['rangeEndDate'] ||
      ch['min'] ||
      ch['max'] ||
      ch['disableWeekends']
    ) {
      this.buildGrid();
    }
  }

  private buildYearRange(): void {
    if (this.yearRangeList && this.yearRangeList.length > 0) {
      this.yearRange = this.yearRangeList;
    } else {
      const base = this.viewYear;
      this.yearRange = Array.from({ length: 40 }, (_, i) => base - 15 + i);
    }
  }
  private position(): void {
    if (!this.anchorRect) return;
    const panelH = 380,
      panelW = 296;
    const vw = window.innerWidth,
      vh = window.innerHeight;

    let top = this.anchorRect.bottom + 6;
    let left = this.anchorRect.left;

    if (top + panelH > vh) top = this.anchorRect.top - panelH - 6;
    if (left + panelW > vw) left = vw - panelW - 10;

    // detect sidebar width dynamically and keep calendar within content area
    const sidebarEl = document.querySelector('app-sidebar') as HTMLElement | null;
    const minLeft = sidebarEl ? sidebarEl.getBoundingClientRect().right + 8 : 8;
    if (left < minLeft) left = minLeft;
    if (top < 8) top = 8;

    this.panelTop = top;
    this.panelLeft = left;
  }

  buildGrid(): void {
    const cells: CalCell[] = [];
    const firstDay = new Date(this.viewYear, this.viewMonth, 1);

    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;

    const prevMonthLastDay = new Date(this.viewYear, this.viewMonth, 0);
    for (let i = startDow - 1; i >= 0; i--) {
      const d = prevMonthLastDay.getDate() - i;
      const y = prevMonthLastDay.getFullYear();
      const m = prevMonthLastDay.getMonth();
      cells.push(this.makeCell(y, m, d, true));
    }

    const daysInMonth = new Date(
      this.viewYear,
      this.viewMonth + 1,
      0,
    ).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(this.makeCell(this.viewYear, this.viewMonth, d, false));
    }

    let nextDay = 1;
    while (cells.length < 42) {
      const nextDate = new Date(this.viewYear, this.viewMonth + 1, nextDay);
      cells.push(
        this.makeCell(
          nextDate.getFullYear(),
          nextDate.getMonth(),
          nextDay,
          true,
        ),
      );
      nextDay++;
    }

    this.cells = cells;
    this.cdr.markForCheck();
  }

  private isWeekend(y: number, m: number, d: number): boolean {
    const date = new Date(y, m, d);
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  private getDayOfWeek(y: number, m: number, d: number): number {
    const date = new Date(y, m, d);
    return date.getDay();
  }

  private makeCell(
    y: number,
    m: number,
    d: number,
    otherMonth: boolean,
  ): CalCell {
    const iso = this.padISO(y, m, d);
    const isToday = iso === this.todayISO;
    const isSelected = !!this.tempSelectedDate && this.tempSelectedDate === iso;
    const isWeekend = this.isWeekend(y, m, d);
    const dayOfWeek = this.getDayOfWeek(y, m, d);

    let disabled = this.isCellDisabled(iso);

    if (this.disableWeekends && isWeekend) {
      disabled = true;
    }

    let inRange = false;
    let isRangeStart = false;
    let isRangeEnd = false;

    if (this.rangeStartDate && this.rangeEndDate && !disabled) {
      const rangeStart = this.rangeStartDate;
      const rangeEnd = this.rangeEndDate;

      if (rangeStart <= iso && iso <= rangeEnd) {
        inRange = true;
        isRangeStart = iso === rangeStart;
        isRangeEnd = iso === rangeEnd;
      }
    } else if (
      this.isEndDateCalendar &&
      this.rangeStartDate &&
      this.hoveredDate &&
      !disabled
    ) {
      const startDate = this.rangeStartDate;
      const hoverDate = this.hoveredDate;

      if (startDate <= iso && iso <= hoverDate) {
        inRange = true;
        isRangeStart = iso === startDate;
        isRangeEnd = iso === hoverDate;
      }
    }

    return {
      day: d,
      iso,
      isToday,
      isSelected,
      otherMonth,
      disabled,
      inRange,
      isRangeStart,
      isRangeEnd,
      isWeekend,
      dayOfWeek,
    };
  }

  getFilteredDayLabels(): string[] {
    if (this.disableWeekends) {
      return this.weekdayLabels;
    }
    return this.dayLabels;
  }

  getFilteredCells(): CalCell[] {
    if (this.disableWeekends) {
      return this.cells.filter((cell) => !cell.isWeekend);
    }
    return this.cells;
  }

  private dateToISO(date: Date): string {
    return this.padISO(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private padISO(y: number, m: number, d: number): string {
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  private parseISO(
    iso: string,
  ): { year: number; month: number; day: number } | null {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
    const [y, m, d] = iso.split('-').map(Number);
    return { year: y, month: m - 1, day: d };
  }

  private isCellDisabled(iso: string): boolean {
    if (this.min && iso < this.min) return true;
    if (this.max && iso > this.max) return true;
    return false;
  }

  onCellHover(cell: CalCell): void {
    if (cell.disabled) return;

    if (this.isEndDateCalendar && this.rangeStartDate) {
      this.hoveredDate = cell.iso;
      this.buildGrid();
    }
  }

  selectDate(cell: CalCell): void {
    if (cell.disabled) return;

    this.tempSelectedDate = cell.iso;
    this.hoveredDate = null;

    if (cell.otherMonth) {
      const parts = this.parseISO(cell.iso)!;
      this.viewMonth = parts.month;
      this.viewYear = parts.year;
    }

    this.buildGrid();
  }

  onDoneClick(): void {
    this.dateSelected.emit(this.tempSelectedDate || '');
    this.close.emit();
  }

  prevMonth(): void {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear--;
    } else this.viewMonth--;
    this.hoveredDate = null;
    this.buildGrid();
  }

  nextMonth(): void {
    if (this.isNextMonthDisabled()) return;

    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear++;
    } else this.viewMonth++;
    this.hoveredDate = null;
    this.buildGrid();
  }

  goToday(): void {
    const today = new Date();
    this.viewMonth = today.getMonth();
    this.viewYear = today.getFullYear();
    const iso = this.dateToISO(today);

    let isTodayDisabled = this.isCellDisabled(iso);
    if (
      this.disableWeekends &&
      this.isWeekend(today.getFullYear(), today.getMonth(), today.getDate())
    ) {
      isTodayDisabled = true;
    }

    if (!isTodayDisabled) {
      this.tempSelectedDate = iso;
      this.hoveredDate = null;
    }
    this.buildGrid();
  }

  clearDate(): void {
    this.tempSelectedDate = null;
    this.hoveredDate = null;
    this.buildGrid();
    this.dateSelected.emit('');
    this.close.emit();
  }

  isNextMonthDisabled(): boolean {
    if (!this.max) return false;

    const maxParts = this.parseISO(this.max);
    if (!maxParts) return false;

    return (
      this.viewYear > maxParts.year ||
      (this.viewYear === maxParts.year && this.viewMonth >= maxParts.month)
    );
  }

  toggleMonthDropdown(e: MouseEvent): void {
    e.stopPropagation();
    const wasOpen = this.showMonthDrop;
    this.showMonthDrop = !wasOpen;
    this.showYearDrop = false;
    this.cdr.markForCheck();
  }

  toggleYearDropdown(e: MouseEvent): void {
    e.stopPropagation();
    const wasOpen = this.showYearDrop;
    this.showYearDrop = !wasOpen;
    this.showMonthDrop = false;
    this.cdr.markForCheck();
    if (!wasOpen) {
      setTimeout(() => this.scrollYearIntoView(), 30);
    }
  }

  selectMonth(index: number, e: MouseEvent): void {
    e.stopPropagation();
    this.viewMonth = index;
    this.showMonthDrop = false;
    this.hoveredDate = null;
    this.buildGrid();
    this.cdr.markForCheck();
  }

  selectYear(year: number, e: MouseEvent): void {
    e.stopPropagation();
    this.viewYear = year;
    this.showYearDrop = false;
    this.hoveredDate = null;
    this.buildGrid();
    this.cdr.markForCheck();
  }

  private scrollYearIntoView(): void {
    try {
      const panel = this.el.nativeElement.querySelector('.year-list');
      if (!panel) return;
      const active = panel.querySelector(
        '.cal-drop-item.active',
      ) as HTMLElement;
      if (active) {
        active.scrollIntoView({ block: 'center' });
      }
    } catch {}
  }

  onBackdropClick(e: MouseEvent): void {
    if (this.showMonthDrop || this.showYearDrop) {
      this.showMonthDrop = false;
      this.showYearDrop = false;
      this.cdr.markForCheck();
      return;
    }
    this.close.emit();
  }

  onPanelClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!target.closest('.cal-dropdown-wrap')) {
      this.showMonthDrop = false;
      this.showYearDrop = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.showMonthDrop || this.showYearDrop) {
      this.showMonthDrop = false;
      this.showYearDrop = false;
      this.cdr.markForCheck();
    } else {
      this.close.emit();
    }
  }
}

interface CalCell {
  day: number;
  iso: string;
  isToday: boolean;
  isSelected: boolean;
  otherMonth: boolean;
  disabled: boolean;
  inRange?: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
  isWeekend?: boolean;
  dayOfWeek?: number;
}
